import { type ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib';
import { GLYPH } from '../glyphs';

export interface FormFieldProps {
  label: ReactNode;
  /** id контрола — для <label htmlFor>. */
  htmlFor?: string;
  /** id подписи, если контрол — не нативный элемент (aria-labelledby). */
  labelId?: string;
  required?: boolean;
  /** Счётчик символов справа от подписи: «12 / 300». */
  counter?: { length: number; max: number };
  /** Элемент справа в строке подписи (например, переключатель языка ввода LocalizedText). */
  aside?: ReactNode;
  error?: string;
  errorId?: string;
  hint?: ReactNode;
  hintId?: string;
  /** Поле на всю строку сетки группы. */
  wide?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * Обёртка поля (README «Оверлеи → Модальное окно»): подпись 12 500 ink-600, обязательное — «∗»
 * wine-bordeaux, счётчик символов справа (11 ink-400); ошибка — 11/16 status-error; подсказка — 11/16 ink-500.
 */
export function FormField({
  label,
  htmlFor,
  labelId,
  required = false,
  counter,
  aside,
  error,
  errorId,
  hint,
  hintId,
  wide = false,
  className,
  children,
}: FormFieldProps) {
  const { t } = useTranslation('form');
  return (
    <div
      className={cn('flex min-w-0 flex-col gap-4', wide ? 'col-span-full' : undefined, className)}
    >
      <div className="flex items-baseline gap-5">
        <label
          id={labelId}
          htmlFor={htmlFor}
          className="text-[12px] font-medium text-pretty text-ink-600"
        >
          {label}
        </label>
        {required ? (
          <span className="text-[13px] leading-[12px] text-wine-bordeaux" title={t('requiredMark')}>
            {GLYPH.required}
          </span>
        ) : null}
        <span className="flex-1" />
        {aside}
        {counter ? (
          <span className="text-[11px] whitespace-nowrap text-ink-400 tabular">
            {t('counter', { length: counter.length, max: counter.max })}
          </span>
        ) : null}
      </div>
      {children}
      {error ? (
        <span id={errorId} className="text-[11px] leading-[16px] text-status-error">
          {error}
        </span>
      ) : hint ? (
        <span id={hintId} className="text-[11px] leading-[16px] text-pretty text-ink-500">
          {hint}
        </span>
      ) : null}
    </div>
  );
}

export interface FieldGroupProps {
  title?: ReactNode;
  actions?: ReactNode;
  className?: string;
  children: ReactNode;
}

/** Группа полей: подзаголовок 11 500 ink-400 с линией line-hair, сетка 2 колонки, gap 12 14. */
export function FieldGroup({ title, actions, className, children }: FieldGroupProps) {
  const titleId = useId();
  return (
    <fieldset
      aria-labelledby={title ? titleId : undefined}
      className={cn('mb-16 flex min-w-0 flex-col gap-10 last:mb-0', className)}
    >
      {title ? (
        <div className="flex items-center gap-12">
          <span
            id={titleId}
            className="text-[11px] font-medium tracking-[0.04em] whitespace-nowrap text-ink-400"
          >
            {title}
          </span>
          <span aria-hidden="true" className="h-1 flex-1 bg-line-hair" />
          {actions}
        </div>
      ) : null}
      <div className="grid grid-cols-2 gap-x-14 gap-y-12">{children}</div>
    </fieldset>
  );
}

/** Поле только для чтения (README «static»): 34, radius 16, фон surface-sunken, рамка line-strong, 13 ink-700. */
export function StaticValue({
  children,
  id,
  multiline = false,
}: {
  children: ReactNode;
  id?: string;
  /** Несколько строк (пары «ключ → значение», списки): высота по содержимому, без обрезки. */
  multiline?: boolean;
}) {
  return (
    <div
      id={id}
      className={cn(
        'flex min-w-0 rounded-16 border border-line-strong bg-surface-sunken px-10',
        multiline ? 'min-h-34 items-start py-8' : 'h-34 items-center',
      )}
    >
      <span
        className={cn(
          'text-[13px] text-ink-700',
          multiline ? 'block w-full min-w-0 [overflow-wrap:anywhere]' : 'truncate',
        )}
      >
        {children}
      </span>
    </div>
  );
}
